import './order-price-summary.css';

import EventEmitter from "eventemitter3";
import { EventTypes } from '../../../core/event-bus';
import { Order } from '../../../models/order';
import { Util } from '../../../utils/util';
import { IName } from '../../../interfaces/global.interfaces';
import { ILoyaltyProvider } from '../../../services/loyalty-provider/loyalty-provider.interface';
import { ProfileService } from '../../../services/profile-service';
import { IIncompleteDiscount, IOrderCoupon, IOrderPayment } from '../../../models/order.interface';
import { IOrderPriceSummaryLocations } from './order-price-summary.interface';
import { Data } from '../../../models/data';
import { Context } from '../../../models/context';
import { Coupon } from '../../../models/coupons';
import { LocalizationService } from '../../../services/localization-service';
import { ICheckCouponHasQualifyingItemsParams } from '@ts/models/coupons.interface';
import { InOrder } from '../../../online-ordering/interfaces/online-ordering.interface';
import { PaymentPaymentType } from '../../../types/enums';

/**
 * Handles rendering of the order price summary, including subtotal, discounts, taxes, and totals.
 */
export class OrderPriceSummary {
    private readonly _eventBus: EventEmitter;
    private readonly _parentDiv: string;
    private readonly _data: Data;
    private readonly _context: Context;
    private readonly _localizationService: LocalizationService;
    private readonly _order: Order;
    private readonly _loyaltyProvider: ILoyaltyProvider;
    private readonly _profileService: ProfileService;
    private readonly _locations: IOrderPriceSummaryLocations | undefined;

    /**
     * Initializes the OrderPriceSummary class.
     * @param {string} parentDiv - The ID of the parent div where the summary will be rendered.
     * @param {Order} order - The current order instance.
     * @param {ILoyaltyProvider} loyaltyProvider - The loyalty provider instance for handling rewards.
     * @param {IProfileService} profileService - The profile service for handling user rewards
     */
    constructor(eventBus: EventEmitter, parentDiv: string, data: Data, context: Context, localizationService: LocalizationService, order: Order, loyaltyProvider: ILoyaltyProvider, profileService: ProfileService, locations?: IOrderPriceSummaryLocations) {
        this._eventBus = eventBus;
        this._parentDiv = parentDiv;
        this._eventBus = eventBus;
        this._data = data;
        this._context = context;
        this._localizationService = localizationService;
        this._order = order;
        this._loyaltyProvider = loyaltyProvider;
        this._profileService = profileService;
        this._locations = locations;
        this._eventBusListeners();
    }

    /**
     * Registers event listeners for the OrderPriceSummary component.
     */
    private _eventBusListeners(): void {
        this._eventBus.on(EventTypes.CART_CHANGED, this._reRenderCart);
    }

    /**
     * Initializes the component by rendering the order price summary.
     */
    public init(nOrder?: InOrder): void {
        this._renderComponent(nOrder);
    }

    /**
     * Re-renders the order price summary when the cart changes.
     * Prevents re-rendering if the user is in the confirmation step.
     */
    private readonly _reRenderCart = (): void => {
        if (this._locations?.isConfirmation)
            return;

        this._renderOrderPriceSummary();
    }

    /**
     * Renders the entire order price summary.
     * @private
     */
    private _renderComponent(nOrder?: InOrder): void {
        this._renderOrderPriceSummary(nOrder);
    }

    /**
     * Renders the entire order price summary.
     * @private
     */
    private _renderOrderPriceSummary(nOrder? : InOrder): void {
        const parentDiv = document.getElementById(this._parentDiv);
        if (!parentDiv)
            return;

        parentDiv.innerHTML = "";

        this._order.calculateTotals();

        const nextToItemTaxTotal = this._calculateNextToItemTaxTotal();
        const subtotal = this._calculateSubtotal(nextToItemTaxTotal);

        parentDiv.appendChild(this._createSummaryRow("Subtotal", subtotal));

        this._renderDiscountSection(parentDiv);
        this._renderOrderTypeCharge(parentDiv);
        this._renderServiceCharges(parentDiv);
        this._renderDiscountedTotal(parentDiv);
        this._renderTaxes(parentDiv);
        this._renderTip(parentDiv);
        this._renderDonations(parentDiv);

        const total = this._createSummaryRow("Total", this._order.calculateOrderTotal(), true);
        parentDiv.appendChild(total);

        this._renderPayments(parentDiv, nOrder);

        // 2DO: Move to checkout page
        //if (update_CHKOUT_Tip) {
        //    Util.setElement("value", "txt_checkout_tip", Util.formatNumber(localAOLO.Order.Tip));
        //    Util.setElement("value", "txt_APP_CHKOUT_Tip", Util.formatNumber(localAOLO.Order.Tip));
        //}

        //Util.setElement("innerText", "spn_checkout_tip_total", Util.formatNumber(localAOLO.Order.Total - localAOLO.Order.TotalDonation));
        //Util.setElement("innerText", "div_APP_CHKOUT_Total", Util.formatNumber(localAOLO.Order.Total));
        //Util.setElement("innerText", "div_checkout_amount_due", Util.formatMoney(localAOLO.Order.AmountDue));
    }

    /**
     * Calculates the total tax displayed next to items.
     * @private
     * @returns {number} - The total tax amount.
     */
    private _calculateNextToItemTaxTotal(): number {
        return this._order.getProperty("Items").reduce((total: number, item: any) => {
            return total + item.Taxes.reduce((itemTotal: number, tax: any) => {
                return tax.DisplayNextToItem ? itemTotal + tax.Amount : itemTotal;
            }, 0);
        }, 0);
    }

    /**
     * Calculates the subtotal of the order.
     * @private
     * @param {number} nextToItemTaxTotal - The total tax displayed next to items.
     * @returns {number} - The calculated subtotal.
     */
    private _calculateSubtotal(nextToItemTaxTotal: number): number {
        return (this._order.getProperty("SubTotal") + nextToItemTaxTotal - this._order.getProperty("TaxInSubTotal"));
    }

    /**
    * Renders the discount section.
    * @private
    * @param {HTMLElement} parentDiv - The parent div where the discounts will be rendered.
    */
    private _renderDiscountSection(parentDiv: HTMLElement): void {
        const discounts = this._order.getProperty("Discount");
        if (discounts <= 0 && this._order.getIncompleteDiscounts().length === 0)
            return;

        const html = `
                      <div id="div_cart_discounts_block" class="discounts-block">
                        <span ltag="Savings">${this._localizationService.names("Savings")}</span>
                        <div id="div_cart_discounts_block_coupons" class="discounts-block-coupons"></div>
                         <div id="div_cart_discounts_block_incomplete" class="discounts-incomplete-block"></div>
                      </div>`;
        if (this._locations?.isCart) {
            const element = Util.createHtmlElementFromTemplate(html);
            const couponSection = element.querySelector("#div_cart_discounts_block_coupons") as HTMLElement;
            if (couponSection) {
                for (const coupon of this._order.getProperty("Coupons")) {
                    couponSection.appendChild(this._createCouponRow(coupon));
                }
            }
            const incompleteSection = element.querySelector("#div_cart_discounts_block_incomplete") as HTMLElement;
            if (incompleteSection)
                this._displayIncompleteDiscounts(incompleteSection);
            parentDiv.appendChild(element);
        } else {
            parentDiv.appendChild(this._createSummaryRow(this._localizationService.names("Savings"), -discounts, false, true));
        }


    }

    /**
     * Creates a row for a coupon.
     * @private
     * @param {IOrderCoupon} coupon - The coupon object.
     * @returns {HTMLElement} - The coupon row element.
     */
    private _createCouponRow(coupon: IOrderCoupon): HTMLElement {
        const couponData = this._order.couponGetCoupon(coupon.CouponId, false, null, coupon.ChannelID);

        const removeHtml = this._locations?.isCart ? `
            <button id="btn_order_cart_coupon_remove_${coupon.CouponKey}" class="button-link"
                    aria-label="remove" ltag="Remove" title="${this._localizationService.names("Remove")}">
                ${this._localizationService.names("Remove")}
            </button>` : "";

        const html = `
            <div class="discounts-block-coupon">
                <div class="coupon-description" ltagj="${Util.toLtagj(couponData?.Names || [])}">
                    ${this._localizationService.GetName(couponData?.Names || [])} $${coupon.Amount.toFixed(2)}
                </div>
                <div class="coupon-actions">
                    <span class="coupon-amount">(${Util.formatMoney(coupon.Amount, this._data.getProperty("StoreInfo").country)})</span>
                    ${removeHtml}
                </div>
            </div>`;

        const element = Util.createHtmlElementFromTemplate(html);
        const removeButton = element.querySelector(`#btn_order_cart_coupon_remove_${coupon.CouponKey}`) as HTMLButtonElement | null;
        if (removeButton)
            removeButton.onclick = async () => { await this._handleCouponRemoveAsync(coupon.CouponKey); };

        return element;
    }

    private readonly _handleCouponRemoveAsync = async (couponKey: number): Promise<void> => {
        const coupon = this._order.getProperty("Coupons").find(x => x.CouponKey === couponKey);
        if (!coupon)
            return;

        await this._handleCouponRemovalAsync(coupon);
    }

    /**
     * Handles coupon removal logic.
     * @private
     * @param {IOrderCoupon} coupon - The coupon to be removed.
     */
    private readonly _handleCouponRemovalAsync = async (coupon: IOrderCoupon): Promise<void> => {
        const loyaltyData = this._profileService.user.getProperty("LoyaltyData");

        // Check if coupon is reward
        if (loyaltyData?.Rewards.some(reward => reward.RewardId === coupon.RewardId)) {
            const reward = loyaltyData.Rewards.find(x => x.RewardId == coupon.RewardId);
            if (reward) {
                await this._loyaltyProvider.removeReward(reward);
                this._eventBus.emit(EventTypes.REWARD_REMOVED, reward.RewardId, false);
                return;
            }
        }

        // Check if coupon is banked currency
        if (coupon.IsBankedCurrency) {
            await this._loyaltyProvider.removeBankedCurrency();
            return;
        }

        // Check if coupon is an offer
        const offers = this._profileService.user.getProperty("Offers");
        if (offers && offers.Codes.length > 0) {
            const offer = offers.Codes.find(x => x.CouponCode.toLowerCase() == Coupon.SanitizeSucCouponCode(coupon.CouponCode));
            if (offer)
                offer.Used = false;
        }

        // Check if coupon is a credit
        const credits = this._profileService.user.getProperty("NextOrderCredits");
        if (credits && credits.length > 0) {
            const credit = credits.find(x => x.CustomerCreditID.toString() === coupon.CouponCode && x.CouponID === coupon.CouponId);
            if (credit)
                credit.Used = false;
        }

        await this._order.removeCouponByKeyAsync(this._loyaltyProvider, coupon.CouponKey, true, true);
    }

    /**
     * Renders the order type charge (e.g., delivery charge).
     * @private
     * @param {HTMLElement} parentDiv - The parent div where the charge will be rendered.
     */
    private _renderOrderTypeCharge(parentDiv: HTMLElement): void {
        const orderTypeCharge = this._order.getProperty("OrderTypeCharge");
        if (orderTypeCharge > 0)
            parentDiv.appendChild(this._createSummaryRow("DeliveryCharge", orderTypeCharge));
    }

    /**
     * Renders the service charges for the order.
     * @private
     * @param {HTMLElement} parentDiv - The parent div where the service charges will be rendered.
     */
    private _renderServiceCharges(parentDiv: HTMLElement): void {
        const serviceCharges = this._order.getProperty("ServiceCharges");
        if (serviceCharges.Amount > 0) {
            for (const serviceCharge of serviceCharges.Detail) {
                const element = this._createSummaryRowWithNames(serviceCharge.Names, serviceCharge.Amount, this._context.getProperty("languageCode"));
                parentDiv.appendChild(element);
            }
        }
    }

    /**
     * Renders the discounted total for the order.
     * @private
     * @param {HTMLElement} parentDiv - The parent div where the discounted total will be rendered.
     */
    private _renderDiscountedTotal(parentDiv: HTMLElement): void {
        const discount = this._order.getProperty("Discount");
        if (discount > 0) {
            const discountedTotal =
                this._order.getProperty("SubTotal") -
                discount +
                this._order.getProperty("ServiceCharges").Amount -
                this._order.getProperty("TaxInSubTotal");
            parentDiv.appendChild(this._createSummaryRow("DiscountedTotal", discountedTotal));
        }
    }

    /**
     * Renders the taxes for the order.
     * @private
     * @param {HTMLElement} parentDiv - The parent div where taxes will be rendered.
     */
    private _renderTaxes(parentDiv: HTMLElement): void {
        const taxes = this._order.getProperty("TaxesDetail");
        for (const tax of taxes) {
            if (tax.TaxTypeName) {
                const element = this._createSummaryRowWithNames(tax.TaxTypeName, tax.Amount, this._context.getProperty("languageCode"));
                parentDiv.appendChild(element);
            }
        }
    }

    /**
     * Renders the tip for the order.
     * @private
     * @param {HTMLElement} parentDiv - The parent div where the tip will be rendered.
     */
    private _renderTip(parentDiv: HTMLElement): void {
        const tip = this._order.getProperty("Tip");
        if (tip > 0)
            parentDiv.appendChild(this._createSummaryRow("Tip", tip));
    }

    /**
     * Renders donations for the order.
     * @private
     * @param {HTMLElement} parentDiv - The parent div where donations will be rendered.
     */
    private _renderDonations(parentDiv: HTMLElement): void {
        const totalDonations = this._order.getProperty("TotalDonation");
        if (totalDonations > 0)
            parentDiv.appendChild(this._createSummaryRow("Donation", totalDonations));
    }

    /**
     * Renders payment totals for order #65472
     * @private
     * @param {HTMLElement} parentDiv - The parent div where payments will be rendered.
     */
    private _renderPayments(parentDiv: HTMLElement, nOrder?: InOrder): void {
        if (nOrder?.Payments?.length)
            this._renderOrderPayments(parentDiv, nOrder.Payments);
        else
            this._renderGiftCardFallbackRows(parentDiv);
    }

    /**
     * Renders a row for each payment on the order.
     * @private
     */
    private _renderOrderPayments(parentDiv: HTMLElement, payments: IOrderPayment[]): void {
        for (const payment of payments) {
            const row = this._createPaymentRow(payment);
            if (row)
                parentDiv.appendChild(row);
        }
    }

    /**
     * Creates the summary row for a single payment, or null for an unrecognized payment type.
     * @private
     */
    private _createPaymentRow(payment: IOrderPayment): HTMLElement | null {
        switch (payment.PaymentTypeID) {
            case PaymentPaymentType.CASH:
                return this._createSummaryRow("Cash", payment.Amount);
            case PaymentPaymentType.CREDIT_CARD_WALLET:
                return this._createSummaryRow("PaidWithSavedCreditCard", payment.Amount);
            case PaymentPaymentType.CREDIT_CARD: {
                const { last4, cardName } = Util.ResolveCreditCardDisplay(payment);
                return this._createCreditCardPaymentRow("CreditCard", payment.Amount, `${cardName} (${last4})`);
            }
            case PaymentPaymentType.CREDIT_CARD_ON_DELIVERY:
                return this._createSummaryRow("ToBePaidByCreditCardUponDelivery", payment.Amount);
            case PaymentPaymentType.GIFT_CARD:
                return this._createSummaryRow("GiftCard", payment.Amount, false, false, ` (${payment.AccountID.slice(-4)})`);
            default:
                return null;
        }
    }

    /**
     * Renders gift-card-only summary rows when the order has no Payments (e.g. fully paid by gift card).
     * @private
     */
    private _renderGiftCardFallbackRows(parentDiv: HTMLElement): void {
        const giftCards = this._order.getProperty("GiftCards");
        if (giftCards.length === 0)
            return;

        for (const giftCard of giftCards) {
            const last4 = giftCard.cardNo?.slice(-4);
            parentDiv.appendChild(this._createSummaryRow("GiftCard", giftCard.appliedAmount, false, false, ` (${last4})`));
        }
    }

    /**
     * Renders a summary row for the order price summary.
     * @private
     * @param {string} label - The label of the row.
     * @param {number} value - The value of the row.
     * @param {boolean} isTotal - Indicates if this is a total row.
     * @returns {HTMLElement} - The created row element.
     */
    private _createSummaryRow(label: string, value: number, isTotal: boolean = false, isDiscount: boolean = false, labelAddon : string = ""): HTMLElement {
        let className = "summary-row";
        if (isTotal) {
            className = "summary-total";
        } else if (isDiscount) {
            className = "discounts-block-checkout";
        }
        const html = `
            <div class="${className}">
                <span ltag="${label}">${this._localizationService.names(label)}${labelAddon}</span>
                <span>${Util.formatMoney(value, this._data.getProperty("StoreInfo").country)}</span>
            </div>`;

        return Util.createHtmlElementFromTemplate(html);
    }

    /**
     * Renders a CC summary row for the order price summary
     * @private
     * @param {string} label - The label of the row.
     * @param {number} value - The value of the row.
     * @returns {HTMLElement} - The created row element.
     */
    private _createCreditCardPaymentRow(label: string, value: number, labelAddon: string = ""): HTMLElement {
        let className = "summary-row";
        
        const html = `
            <div class="${className}">
                <span ltag="${label}">${labelAddon}</span>
                <span>${Util.formatMoney(value, this._data.getProperty("StoreInfo").country)}</span>
            </div>`;

        return Util.createHtmlElementFromTemplate(html);
    }

    /**
     * Creates a summary row for displaying a pair of names and a value.
     * @private
     * @param {IName[]} names - The localized names for the item.
     * @param {number} value - The monetary value to display.
     * @param {string} languageCode - The language code for localization.
     * @returns {HTMLElement} - The created HTML element representing the summary row.
     */
    private _createSummaryRowWithNames(names: IName[], value: number, languageCode: string): HTMLElement {
        const html = `
            <div class="summary-row">
                <span ltagj="${Util.toLtagj(names)}">${this._localizationService.GetName(names)}</span>
                <span>${Util.formatMoney(value, this._data.getProperty("StoreInfo").country)}</span>
            </div>`;

        return Util.createHtmlElementFromTemplate(html);
    }

    /**
     * Displays incomplete discounts in the discounts section.
     * Removes existing incomplete items and renders the current incomplete discounts.
     * @private
     */
    private _displayIncompleteDiscounts(containerDiv: HTMLElement): void {
        containerDiv.innerHTML = "";

        for (const incompleteCoupon of this._order.getIncompleteDiscounts()) {
            const element = this._generateIncompleteDiscountElement(incompleteCoupon);
            if (element)
                containerDiv.appendChild(element);
        }
    }

    /**
     * Generates the HTML for a single incomplete discount.
     * @private
     * @param {IIncompleteDiscount} incompleteCoupon - The incomplete discount object.
     * @returns {string} - The generated HTML for the incomplete discount.
     */
    private _generateIncompleteDiscountElement(incompleteCoupon: IIncompleteDiscount): HTMLElement | null {
        const coupon = this._order.couponGetCoupon(incompleteCoupon.couponId, false, this._order.getProperty("OrderTypeID"), incompleteCoupon.channelId);
        if (!coupon)
            return null;

        const button = coupon.Items.length === 0 ? "" : `
            <button id="btn_cart_incomplete_complete_${incompleteCoupon.couponId}" 
                    name="btn_cart_incomplete_complete" 
                    class="button-link" 
                    title="${this._localizationService.names(`CompleteRequiredItems`)}" 
                    ltag="CompleteRequiredItems">
                ${this._localizationService.names(`CompleteRequiredItems`)}
            </button>`;

        const html = `
            <div name="div_cart_discounts_incomplete" class="discounts-incomplete-item">
                <span ltag="InProgress">${this._localizationService.names("InProgress")}</span>
                <span ltagj="${Util.toLtagj(coupon.Names)}">
                    ${this._localizationService.GetName(coupon.Names)}
                </span>
                <div class="coupon-actions">
                    ${button}
                    <button id="btn_cart_incomplete_remove_${incompleteCoupon.couponId}" 
                            name="btn_cart_incomplete_remove" 
                            class="button-link" 
                            aria-label="remove" 
                            title="${this._localizationService.names(`Remove`)}" 
                            ltag="Remove">
                        ${this._localizationService.names(`Remove`)}
                    </button>
                </div>
            </div>`;

        const element = Util.createHtmlElementFromTemplate(html);

        const removeButton = element.querySelector(`#btn_cart_incomplete_remove_${incompleteCoupon.couponId}`) as HTMLButtonElement;
        if (removeButton)
            removeButton.onclick = async () => {
                await this._order.removeIncompleteCouponByIdAsync(this._loyaltyProvider, incompleteCoupon.couponId);
            };

        const completeButton = element.querySelector(`#btn_cart_incomplete_complete_${incompleteCoupon.couponId}`) as HTMLButtonElement;
        if (completeButton)
            completeButton.onclick = async () => {
                if (!this._order.isCouponAllowedInFundraiser())
                    return;

                await this._processCompleteDiscountAsync(
                    incompleteCoupon.couponId,
                    incompleteCoupon.rewardId || null,
                    incompleteCoupon.couponCode || "");
            };

        return element;
    }

    /**
     * Processes the completion of an incomplete discount.
     * @private
     * @param button - The button element containing the discount information.
     */
    private readonly _processCompleteDiscountAsync = async (couponId: number, rewardId: number | null, couponCode: string): Promise<void> => {
        const reward = rewardId ? this._profileService.user.getRewardInfo(rewardId) : null;
        const sanitizedCode = Coupon.SanitizeSucCouponCode(couponCode);
        const offer = this._profileService.user.getOfferInfo(sanitizedCode!);
        const isCampaignCode = (offer && offer.IsSingleUseCoupon && !offer.IsCouponPromotionCode) || false;
        const isPromotionCode = (offer && offer.IsSingleUseCoupon && offer.IsCouponPromotionCode) || false;

        const params: ICheckCouponHasQualifyingItemsParams = {
            couponId: couponId,
            couponCode: couponCode,
            orderId: null,
            reward: reward,
            channelId: null,
            isCampaignCode: isCampaignCode,
            isPromotionCode: isPromotionCode,
            offer: offer
        };
        await this._order.checkCouponHasQualifyingItemsAsync(this._loyaltyProvider, params);
    }
}